/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { AiCategoriesType, AiModels, Currency, PeriodQueryParams, SearchQueryParams } from '../apiCommon.typings';

export interface DocumentsListFilterParams extends PeriodQueryParams, Partial<SearchQueryParams> {
    taxRates?: TaxRatesAsStrings[];
    paymentTypes?: PaymentTypesAsStrings[];
    sumFrom?: number;
    sumTo?: number;
    docTypeIds?: DocType[];
    docNumbersSearchText?: string;
    docNumbersSearchType?: string;
    restored?: boolean;
}

export interface CashBoxDocumentsTabFilterParams extends PeriodQueryParams, Partial<SearchQueryParams> {
    docTypeIds?: DocType[];
}

export interface DocumentsPageQueryParams {
    afterKeyCreatedAtUtc?: string;
    afterKeyId?: string;
    limit?: number;
}

export type DocumentsListRequestParams = DocumentsListFilterParams & DocumentsPageQueryParams;
export type DocumentsListExcelRequestParams = DocumentsListFilterParams;

export interface DocumentsListDto {
    afterKeyCreatedAtUtc: string;
    afterKeyId: string;
    documents: DocumentsListItemDto[];
}

export interface DocumentsListItemDto {
    documentId?: string;
    documentIdHash?: string;
    docTypeId: DocType;
    docNumber: number;
    createdAtUtc: string;
    companyTaxNumber: string;
    companyName?: string;
    storeTaxNumber: string;
    cashBoxTaxNumber: string;
    sum?: number;
    taxSum?: number;
    taxRates?: string[];
    paymentTypes?: string[];
    parentDocumentId?: string;
    periodFrom?: string;
    periodTo?: string;
    numberOfDocs?: number;
    globalDocumentNumber?: number;
    documentNumberInShift?: number;
    restored?: boolean;
}

export enum DocType {
    Sale = 0,
    Deposit = 1,
    Withdraw = 2,
    MoneyBack = 3,
    RollBack = 4,
    Correction = 5,
    Prepayment = 6,
    CreditPay = 7,
    OpenShift = 19,
    ZReport = 20,
}

export enum DocTypesAsStrings { //not used
    SALE = 'SALE',
    DEPOSIT = 'DEPOSIT',
    WITHDRAW = 'WITHDRAW',
    MONEY_BACK = 'MONEY_BACK',
    ROLLBACK = 'ROLLBACK',
    CORRECTION = 'CORRECTION',
    OPEN_SHIFT = 'OPEN_SHIFT',
    Z_REPORT = 'Z_REPORT',
    PREPAY = 'PREPAY',
    CREDITPAY = 'CREDITPAY',
}

export enum PaymentTypesAsStrings {
    CASH = 'CASH',
    CASHLESS = 'CASHLESS',
    CREDIT = 'CREDIT',
    BONUS = 'BONUS',
    PREPAYMENT = 'PREPAYMENT',
}

export enum TaxRatesAsStrings {
    _0 = '0',
    _2 = '2',
    _8 = '8',
    _18 = '18',
    TM = 'TM',
    OTHER = 'OTHER',
}

export enum TransactionType {
    PURCHASE = 'PURCHASE',
    REVERSE = 'REVERSE',
    REFUND = 'REFUND',
}

export interface TransactionsItem {
    rrn: string;
    amount: number;
    type?: TransactionType;
    approval_code?: string;
}

export interface DocResponse {
    count: number;
    cheque: DocumentResource;
}

export type UnknownDocumentContent =
    | SaleDocumentResource
    | ZReportDocumentResource
    | DepositDocumentResource
    | WithdrawDocumentResource
    | RollBackDocumentResource
    | MoneyBackDocumentResource
    | CorrectionDocumentResource
    | OpenShiftDocumentResource
    | PrepayDocumentResource
    | CreditPayDocumentResource;

export interface DocumentResource<T extends UnknownDocumentContent = UnknownDocumentContent> {
    content: T;
    documentId: string;
    shortDocumentId: string;
    createdAtUtc: string;
    insertedAtUtc: string;
    isIncorrectTime: boolean;
    companyName: string;
    companyTaxNumber: string;
    storeAddress: string;
    storeTaxNumber: string;
    storeName: string;
    storePhone: string;
    cashregisterModelName: string;
    cashregisterFactoryNumber: string;
    cashregisterModelTaxNumber: string;
}

export interface OpenShiftDocumentResource {
    docType: DocType.OpenShift;
    reportNumber: number;
    cashboxTaxNumber: string;
    createdAtUtc: number;
    documentVersion?: number;
    globalDocNumber: number;
    restored?: boolean;
}

export interface SaleDocumentResource
    extends DocumentContentBase,
        InterbankTransactions,
        PaymentTypes,
        CreditContractInfo,
        ProductTable {
    docType: DocType.Sale;
    sum: number;
    vatAmounts: VatAmounts[];
    positionInShift: number;
    parents?: string[];
    carNumber?: string;
    gasStationInfo?: GasStationInfo;
    committentInfo?: CommittentInfo;
    guestsCount?: number;
}

export interface PrepayDocumentResource extends DocumentContentBase, InterbankTransactions, PaymentTypes, ProductTable {
    docType: DocType.Prepayment;
    vatAmounts: VatAmounts[];
    sum: number;
    positionInShift: number;
    committentInfo?: CommittentInfo;
}

export interface CreditPayDocumentResource
    extends DocumentContentBase,
        InterbankTransactions,
        PaymentTypes,
        CreditContractInfo,
        ProductTable {
    docType: DocType.CreditPay;
    vatAmounts: VatAmounts[];
    sum: number;
    positionInShift: number;
    parentDocument: string;
    residue: number;
    paymentNumber?: number;
    committentInfo?: CommittentInfo;
}

export interface ZReportDocumentResource {
    docType: DocType.ZReport;
    currencies: ZReportCurrency[];
    cashboxTaxNumber: string;
    shiftOpenAtUtc: number;
    lastDocNumber: number;
    createdAtUtc: number;
    firstDocNumber: number;
    reportNumber: number;
    docCountToSend?: number;
    currency: Currency;
    documentVersion?: number;
    globalDocNumber: number;
    restored?: boolean;
}

export interface DepositDocumentResource extends DocumentContentBase {
    sum: number;
    docType: DocType.Deposit;
    positionInShift: number;
}

export interface WithdrawDocumentResource extends DocumentContentBase {
    sum: number;
    docType: DocType.Withdraw;
    positionInShift: number;
}

export interface MoneyBackDocumentResource
    extends DocumentContentBase,
        InterbankTransactions,
        PaymentTypes,
        ProductTable {
    parentDocument: string;
    sum: number;
    docType: DocType.MoneyBack;
    vatAmounts: VatAmounts[];
    positionInShift: number;
    moneyBackType: DocType;
    carNumber?: string;
    gasStationInfo?: GasStationInfo;
    committentInfo?: CommittentInfo;
}

export interface RollBackDocumentResource extends DocumentContentBase, InterbankTransactions, PaymentTypes {
    sum: number;
    parentDocument: string;
    docType: DocType.RollBack;
    vatAmounts: VatAmounts[];
    positionInShift: number;
    parentDocumentType: DocType;
    committentInfo?: CommittentInfo;
}

export interface CorrectionDocumentResource
    extends DocumentContentBase,
        InterbankTransactions,
        PaymentTypes,
        ProductTable {
    vatAmounts: VatAmounts[];
    docType: DocType.Correction;
    sum: number;
    firstOperationAtUtc: number;
    lastOperationAtUtc: number;
    positionInShift: number;
    gasStationInfo?: GasStationInfo;
    committentInfo?: CommittentInfo;
}

export interface DocumentContentBase {
    cashboxTaxNumber: string;
    docNumber: number;
    createdAtUtc: number;
    cashier: string;
    documentVersion?: number;
    globalDocNumber: number;
    currency: Currency;
    bonusCardNumber?: string;
    restored?: boolean;
}

export interface InterbankTransactions {
    rrn?: string;
    transactions?: TransactionsItem[];
}

export interface PaymentTypes {
    cashSum: number;
    cashlessSum: number;
    creditSum: number;
    bonusSum: number;
    prepaymentSum: number;
    incomingSum?: number;
    changeSum?: number;
}

export interface VatAmounts {
    vatPercent?: number;
    vatSum: number;
    vatResult: number;
}

export interface CreditContractInfo {
    creditPayer?: string;
    creditContract?: string;
}

export interface GasStationInfo {
    fdp: string;
    pump: string;
    gun: string;
    reservoir: string;
}

export interface CommittentInfo {
    tin: string;
    name: string;
    agentContract: string;
    agentCommission?: number;
}

export interface ProductTable {
    items: ProductItemInfo[];
    itemsHash?: string;
}

export interface ProductItemInfo {
    itemNumber?: number;
    itemName?: string;
    itemQuantity?: number;
    itemQuantityType?: QuantityTypes;
    itemPrice: number;
    itemSum: number;
    itemCode: string;
    itemCodeType: keyof ItemCodeTypes;
    itemMarginPrice?: number;
    itemMarginSum?: number;
    itemVatPercent?: number;
    itemVatAmount?: number;
    itemMarkingCode?: string;
    itemMarkingCodes?: string[];
    itemAgentCommission?: number;
    itemAiCategories?: ProductItemAiCategories;
    itemCreditpaySum?: number;
}

interface ItemCodeTypes {
    PLAIN_TEXT: string;
    EAN8: string;
    EAN13: string;
    SERVICE: number;
    PREPAYMENT: string;
    CREDIT: string;
}

export enum QuantityTypes {
    Things = 0,
    Kilos = 1,
    Liters = 2,
    Meters = 3,
    SquareMeters = 4,
    CubicMeters = 5,
}

export interface ProductItemAiCategories {
    id: number;
    name: string;
    categories: AiCategory[];
}

export interface AiCategory {
    model: AiModels;
    category: AiCategoriesType;
    similarity: number;
}

export interface ZReportCurrency {
    currency: Currency;

    turnover: number;
    turnoverTax: number;

    correctionSum: number;
    correctionCount: number;
    correctionCashSum: number;
    correctionCashlessSum: number;
    correctionPrepaymentSum: number;
    correctionCreditSum: number;
    correctionBonusSum: number;

    moneyBackCount: number;
    moneyBackCashlessSum: number;
    moneyBackCashSum: number;
    moneyBackPrepaymentSum: number;
    moneyBackCreditSum: number;
    moneyBackBonusSum: number;
    moneyBackSum: number;

    rollbackSum: number;
    rollbackCount: number;
    rollbackCashlessSum: number;
    rollbackCashSum: number;
    rollbackPrepaymentSum: number;
    rollbackCreditSum: number;
    rollbackBonusSum: number;

    saleCount: number;
    saleCashSum: number;
    saleSum: number;
    saleCashlessSum: number;
    saleBonusSum: number;
    saleCreditSum: number;
    salePrepaymentSum: number;

    prepayCount: number;
    prepaySum: number;
    prepayCashSum: number;
    prepayCashlessSum: number;
    prepayPrepaymentSum: number;
    prepayCreditSum: number;
    prepayBonusSum: number;

    creditpayCount: number;
    creditpaySum: number;
    creditpayCashSum: number;
    creditpayCashlessSum: number;
    creditpayPrepaymentSum: number;
    creditpayCreditSum: number;
    creditpayBonusSum: number;

    depositCount: number;
    depositSum: number;

    withdrawCount: number;
    withdrawSum: number;

    saleVatAmounts: VatAmounts[];
    moneyBackVatAmounts: VatAmounts[];
    rollbackVatAmounts: VatAmounts[];
    correctionVatAmounts: VatAmounts[];
    prepayVatAmounts: VatAmounts[];
    creditpayVatAmounts: VatAmounts[];
}
